// A labelled form control for Lumen dialogs and forms: the label (with the required "*"), an optional hint under it,
// the control, and an optional error under the control.
//
//   <Field id={id} label={t('…npwp')} required hint={t('…npwpHint')}
//     error={invalid ? t('…npwpInvalid') : undefined}>
//     <Input id={id} aria-required aria-invalid={invalid || undefined}
//       aria-describedby={fieldDescribedBy(id, { hint: true, error: invalid })} />
//   </Field>
//
//   <Field id={id} label={t('…type')}>
//     <OptionSelect id={id} value={form.type} options={TYPE_OPTIONS} onChange={(type) => setForm({ ...form, type })} />
//   </Field>
//
// - `id` is the control's id: the label points at it, the hint and error get `${id}-hint` / `${id}-error`
//   (field-ids.ts). Give the control that same id.
// - The control's own `aria-*` stay on the control, set by the page (see field-ids.ts for why).
// - `className` adds to the wrapper (`min-w-0` in a grid row, `content-start` beside a taller field);
//   `hintClassName` adds to the hint line (`-mt-1` pulls it up against the label).
// - PR 6's Formik wrappers (plan §7.3) will live next to this file and render through it.
import type { ComponentProps, ReactNode } from 'react';

import { Label } from '@/components/lumen/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/lumen/select';
import { cn } from '@/lib/utils';
import { fieldErrorId, fieldHintId } from './field-ids';

export interface FieldProps {
  /** The control's id. */
  id: string;
  label: ReactNode;
  /** Shows the red "*" after the label (aria-hidden; mark the control `aria-required` yourself). */
  required?: boolean;
  /** A line under the label, above the control (Mantine's `description`). */
  hint?: ReactNode;
  /** Classes added to the hint line. */
  hintClassName?: string;
  /** A line under the control, in the error colour. Nothing renders while it is empty. */
  error?: ReactNode;
  /** Classes added to the wrapper (`grid gap-1.5`). */
  className?: string;
  children: ReactNode;
}

export function Field({ id, label, required, hint, hintClassName, error, className, children }: FieldProps) {
  return (
    <div className={cn('grid gap-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span aria-hidden className="text-err">
            *
          </span>
        )}
      </Label>
      {hint && (
        <p id={fieldHintId(id)} className={cn('text-fg-muted text-xs', hintClassName)}>
          {hint}
        </p>
      )}
      {children}
      {error && (
        <p id={fieldErrorId(id)} className="text-err text-xs">
          {error}
        </p>
      )}
    </div>
  );
}

export interface OptionSelectOption {
  value: string;
  label: ReactNode;
}

export interface OptionSelectProps
  extends Omit<ComponentProps<typeof SelectTrigger>, 'children' | 'onChange' | 'value' | 'placeholder'> {
  /** The trigger's id: pass the Field's id. */
  id: string;
  /** The selected value; `undefined` or `''` shows the placeholder. */
  value: string | undefined;
  options: OptionSelectOption[];
  onChange: (value: string) => void;
  /** Shown while nothing is selected. */
  placeholder?: string;
}

/**
 * A plain Lumen Select from an options list, full width in its Field. Other props (`aria-*`, `disabled`,
 * `className`) go to the trigger; `className` adds to `w-full`.
 */
export function OptionSelect({
  id,
  value,
  options,
  onChange,
  placeholder,
  className,
  ...triggerProps
}: OptionSelectProps) {
  return (
    <Select value={value ?? ''} onValueChange={onChange}>
      <SelectTrigger id={id} className={cn('w-full', className)} {...triggerProps}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
